import { Component, DestroyRef, inject, Inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Observable } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { translate } from '@jsverse/transloco';
import { ActivityService } from 'src/app/services/api/activity.service';
import { CustomToastrService } from 'src/app/services/util/toastr.service';
import { DeploymentClass } from 'src/app/models/instance-class';


export interface HealthMetrics {
  thresholdRatio: string;
  elapsedRatio: string;
  warning: string | boolean;
  elapsedDurationMs: number;
}

export interface ActivityStage {
  type: 'agent_deployment' | 'component_execution' | string;
  location: string;
  status: 'success' | 'inprogress' | 'failed' | 'pending' | string;
  startTime: string;
  finishTime?: string;
  expectedDurationMs: number;
  components: any[];
  health?: HealthMetrics;
}

export interface ActivityDetails {
  id: string;
  operationId: string;
  operation: string;
  status: 'success' | 'inprogress' | 'failed' | string;
  opstatus: number;
  startTime: string;
  finishTime: string;
  expectedDurationMs: number;
  stages: ActivityStage[];
  initiatedBy: string;
  httpStatusCode: number;
  instanceClass: DeploymentClass;
}

export interface EnvironmentActivityDialogData {
  instanceId: string | number;
  id: string | number;
}

export interface ApiError {
  message?: string;
  error?: {
    message?: string;
  };
  errmsg?: string;
}

@Component({
  selector: 'app-environment-activity-popup',
  templateUrl: './environment-activity-popup.component.html',
  styleUrls: ['./environment-activity-popup.component.scss'],
  standalone: false
})
export class EnvironmentActivityPopupComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  selectedStage: ActivityStage | undefined;
  activityDetails: ActivityDetails | null = null;
  isDataLoading = true;
  isRefreshing = false;
  lastFetchedAt: Date | null = null;
  selectedStageTitle = '';
  stageTypeText: Record<string, string> = {
    agent_deployment: "Deploy Agent",
    component_execution: "Execute Components"
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<EnvironmentActivityPopupComponent>,
    private activityService: ActivityService,
    private toastrService: CustomToastrService,
  ) {}

  ngOnInit(): void {
    this.loadInitial();
  }

  private loadInitial(): void {
    this.isDataLoading = true;
    this.fetchActivityDetail()
      .pipe(finalize(() => (this.isDataLoading = false)))
      .subscribe();
  }

  private reload(): void {
    if (this.isDataLoading || this.isRefreshing) {
      return;
    }
    this.isRefreshing = true;
    this.fetchActivityDetail()
      .pipe(finalize(() => (this.isRefreshing = false)))
      .subscribe();
  }

  private fetchActivityDetail(): Observable<ActivityDetails> {
    return this.activityService.getInstanceActivityLogsById(this.data?.instanceId, this.data?.id).pipe(
      tap((details:ActivityDetails) => {
        this.activityDetails = details;
        if (this.activityDetails?.stages?.find(item => item.status === 'inprogress')) {
          this.selectedStage = this.activityDetails?.stages?.find(item => item.status === 'inprogress');
        } else if (this.data.instanceClass === DeploymentClass.A) {
          this.selectedStage = this.activityDetails?.stages[2] ? this.activityDetails?.stages[2] : this.activityDetails?.stages[1] ? this.activityDetails?.stages[1] :
            this.activityDetails?.stages[0] ? this.activityDetails?.stages[0] : undefined;
        } else {
          this.selectedStage = this.activityDetails?.stages[1] ? this.activityDetails?.stages[1] : this.activityDetails?.stages[0] ? this.activityDetails?.stages[0] : undefined;
        }
        if (this.selectedStage) {
          const stageIndex = (this.activityDetails?.stages ?? []).indexOf(this.selectedStage);
          this.selectedStageTitle = `Stage ${stageIndex + 1} - ${this.stageTypeText[this.selectedStage.type] || this.selectedStage.type}`;
        }
        this.lastFetchedAt = new Date();
        this.syncSelectedStage();
      }),
      catchError((error:ApiError) => {
        const title = translate('ERRORS.SERVICE_AVAILABILITY');
        const message = error?.message ?? error?.error?.message ?? error?.errmsg ?? translate('SERVICE.INTERNAL_SERVER_ERROR');
        this.toastrService.errMessage(message, title);
        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  private syncSelectedStage(): void {
    if (!this.selectedStage) {
      return;
    }
    const fresh = (this.activityDetails?.stages ?? []).find(
      (stage: ActivityStage) => stage?.type === this.selectedStage?.type && stage?.location === this.selectedStage?.location
    );
    if (fresh) {
      this.selectedStage = fresh;
    }
  }

  onRefresh(): void {
    this.reload();
  }

  onStageSelected(stage: ActivityStage): void {
    this.selectedStage = stage;
  }

  onStageTitleSelected(title: string): void {
    this.selectedStageTitle = title;
  }

  closeDialog(): void {
    this.dialogRef.close();
  }
}